import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaArrowRight, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import ContactForm from '../components/ContactForm';
import ConsultationForm from '../components/ConsultationForm';
import { servicesData } from '../data/servicesData';
import { socialData } from '../data/footerData';
import '../styles/Contact.css';

const Contact = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleFormSubmit = () => {
        setIsSubmitted(true);
    };

    const [showQuoteForm, setShowQuoteForm] = useState(false);

    const handleViewMap = (e) => {
        e.preventDefault();
        // Open Google Maps with your business location
        window.open('https://www.google.com/maps/place/123+Design+Street', '_blank');
    };

    const handleBookAppointment = (e) => {
        e.preventDefault();
        setShowQuoteForm(true);
    };

    const handleCloseQuoteForm = () => {
        setShowQuoteForm(false);
    };

    // Icon mapping function
    const getIcon = (iconName) => {
        const icons = {
            FaPhone: <FaPhone className="contact-icon" />,
            FaEnvelope: <FaEnvelope className="contact-icon" />,
            FaMapMarkerAlt: <FaMapMarkerAlt className="contact-icon" />,
            FaClock: <FaClock className="contact-icon" />
        };
        return icons[iconName] || <FaEnvelope className="contact-icon" />;
    };

    // Social icon mapping function
    const getSocialIcon = (iconName) => {
        const icons = {
            FaFacebookF: <FaFacebookF />,
            FaTwitter: <FaTwitter />,
            FaInstagram: <FaInstagram />,
            FaLinkedinIn: <FaLinkedinIn />,
            FaYoutube: <FaYoutube />
        };
        return icons[iconName] || <FaFacebookF />;
    };

    // Contact info using real footer data
    const contactInfo = [
        {
            icon: getIcon(socialData.contactInfo.phone.icon),
            title: socialData.contactInfo.phone.label,
            content: socialData.contactInfo.phone.value,
            link: socialData.contactInfo.phone.url,
            linkText: "Call Now",
            formField: "phone"
        },
        {
            icon: getIcon(socialData.contactInfo.email.icon),
            title: socialData.contactInfo.email.label,
            content: socialData.contactInfo.email.value,
            link: socialData.contactInfo.email.url,
            linkText: "Send Email",
            formField: "email"
        },
        {
            icon: getIcon(socialData.contactInfo.location.icon),
            title: socialData.contactInfo.location.label,
            content: socialData.contactInfo.location.value,
            onClick: handleViewMap,
            linkText: "View on Map",
            formField: "location"
        },
        {
            icon: <FaClock className="contact-icon" />,
            title: "Business Hours",
            content: [
                "Monday - Friday 9:00AM - 6:00PM",
            ],
            onClick: handleBookAppointment,
            linkText: "Book Consultation",
            formField: "appointment"
        }
    ];

    // Form fields data structure
    const formFields = [
        { name: 'name', label: 'Name', type: 'text', placeholder: 'Your Name', required: true },
        { name: 'email', label: 'Email', type: 'email', placeholder: 'Your Email', required: true },
        { name: 'phone', label: 'Phone', type: 'tel', placeholder: 'Your Phone Number', required: true },
        { name: 'service', label: 'Service', type: 'select', options: ['General Inquiry', ...servicesData.map(s => s.title)] },
        { name: 'message', label: 'Message', type: 'textarea', placeholder: 'How can we help you?', rows: 5 }
    ];

    return (
        <motion.div
            className="contact-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Hero Section with Custom Color and Image */}
            <div className="contact-hero">
                <div className="hero-background">
                    <img src="https://rdsoftware.in/wp-content/uploads/2025/08/service-7.jpg" alt="Luxury Interior" className="hero-bg-image" />
                    <div className="hero-overlay"></div>
                </div>
                <div className="container">
                    <motion.div
                        className="hero-content"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        <h1>Let's Create Something Amazing Together</h1>
                        <p>Holistic interiors aligned with your lifestyle and zodiac—auspicious, aesthetic, and timeless.</p>
                    </motion.div>
                </div>
            </div>

            {/* Combined Contact Section */}
            <div className="contact-container">
                <motion.div
                    className="combined-contact-section"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                >
                    <div className="contact-header">
                        <h2>Get In Touch</h2>
                        <p>Ready to transform your space? Our design experts are here to bring your vision to life with personalized solutions.</p>
                    </div>

                    <div className="contact-main-grid">
                        {/* Left Side - Contact Form */}
                        <div className="contact-form-wrapper">
                            <AnimatePresence mode="wait">
                                {isSubmitted ? (
                                    <motion.div key="success" className="form-success">
                                        <div className="success-icon">✓</div>
                                        <h3>Message Sent Successfully!</h3>
                                        <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                                        <button onClick={() => setIsSubmitted(false)} className="cta-button">
                                            Send Another Message
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div key="form" className="form-container">
                                        <h3>Start Your Project</h3>
                                        <ContactForm initialService="Contact Us" onSubmitSuccess={handleFormSubmit} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Right Side - Contact Info & Services Showcase */}
                        <div className="contact-info-wrapper">
                            {/* Contact Info Cards */}
                            <div className="contact-info-grid">
                                {contactInfo.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className="info-card-compact"
                                        whileHover={{ scale: 1.02 }}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <div className="info-icon">{item.icon}</div>
                                        <div className="info-content">
                                            <h4>{item.title}</h4>
                                            {Array.isArray(item.content) ? (
                                                <ul className="hours-list-compact">
                                                    {item.content.map((time, i) => (
                                                        <li key={i}>{time}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p>{item.content}</p>
                                            )}
                                            {item.link ? (
                                                <a href={item.link} className="info-link">
                                                    {item.linkText} <FaArrowRight className="link-arrow" />
                                                </a>
                                            ) : (
                                                <button onClick={item.onClick} className="info-link">
                                                    {item.linkText} <FaArrowRight className="link-arrow" />
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Featured Services */}
                            <div className="featured-services">
                                <h3>Our Popular Services</h3>
                                <div className="services-compact-grid">
                                    {servicesData.slice(0, 4).map((service, index) => (
                                        <motion.div
                                            key={service.id}
                                            className="service-compact-card"
                                            whileHover={{ y: -3 }}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.4 + index * 0.1 }}
                                        >
                                            <div className="service-compact-image">
                                                <img src={service.imageUrl} alt={service.title} />
                                                <div className="service-compact-overlay">
                                                    <div className="service-compact-icon" style={{ color: service.iconColor }}>
                                                        {service.icon}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="service-compact-content">
                                                <h4>{service.title}</h4>
                                                <p className="service-compact-price">{service.startingPrice}</p>
                                                <div className="service-compact-meta">
                                                    <span className="duration">{service.duration}</span>
                                                    <span className="rating">{'★'.repeat(Math.floor(parseFloat(service.rating)))}</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Consultation Form Modal */}
            <ConsultationForm
                isOpen={showQuoteForm}
                onClose={handleCloseQuoteForm}
                initialService="General Consultation"
            />
        </motion.div>
    );
};

export default Contact;
